export type StoredActiveCheckinSession = {
  id: string;
  assignmentId: string;
  campaignId: string;
  shiftId: string;
  campaignStoreId: string | null;
  storeId: string | null;
  baId: string;
  promoterUserId: string;
  checkedInAt: string;
  checkedOutAt?: string | null;
  status: string;
  deviceId?: string | null;
  campaignName?: string;
  shiftRole?: string | null;
  shiftStartsAt?: string | null;
  shiftEndsAt?: string | null;
  storeName?: string | null;
  storeAddress?: string | null;
  storeLat?: number | null;
  storeLng?: number | null;
  geoRadiusMeters?: number | null;
  source?: 'server' | 'local';
};

export type StoredCheckoutSummary = {
  sessionId: string;
  campaignId: string;
  campaignName: string;
  checkedInAt: string;
  checkedOutAt: string;
  scheduledMinutes: number;
  workedMinutes: number;
  varianceMinutes: number;
  status: string;
  note?: string | null;
  actionHref?: string | null;
  actionLabel?: string | null;
};

const ACTIVE_SHIFT_STORAGE_KEY = 'ba-active-checkin-session';
const CHECKOUT_SUMMARY_STORAGE_KEY = 'ba-checkout-summary';

const canUseStorage = () => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const readJson = <T,>(key: string): T | null => {
  if (!canUseStorage()) return null;

  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch (error) {
    console.warn('[active-shift-storage] failed to parse item', { key, error });
    return null;
  }
};

const writeJson = (key: string, value: unknown) => {
  if (!canUseStorage()) return;

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.warn('[active-shift-storage] failed to persist item', { key, error });
  }
};

const removeItem = (key: string) => {
  if (!canUseStorage()) return;

  try {
    window.localStorage.removeItem(key);
  } catch (error) {
    console.warn('[active-shift-storage] failed to clear item', { key, error });
  }
};

export const getStoredActiveCheckinSession = () =>
  readJson<StoredActiveCheckinSession>(ACTIVE_SHIFT_STORAGE_KEY);

export const setStoredActiveCheckinSession = (session: StoredActiveCheckinSession) => {
  writeJson(ACTIVE_SHIFT_STORAGE_KEY, session);
};

export const clearStoredActiveCheckinSession = () => {
  removeItem(ACTIVE_SHIFT_STORAGE_KEY);
};

export const getStoredCheckoutSummary = () =>
  readJson<StoredCheckoutSummary>(CHECKOUT_SUMMARY_STORAGE_KEY);

export const setStoredCheckoutSummary = (summary: StoredCheckoutSummary) => {
  writeJson(CHECKOUT_SUMMARY_STORAGE_KEY, summary);
};

export const clearStoredCheckoutSummary = () => {
  removeItem(CHECKOUT_SUMMARY_STORAGE_KEY);
};
